import { useContext, useEffect } from 'react';
import { atom, useRecoilState } from 'recoil';
import { DataContext } from 'entries/common/widgets/saved-project-configuration/context';
import { TCheckedType, TProjectConfigurationGeneralItem } from 'pages/common/types';
import { TService } from 'pages/dimensions/store/dimensions';
import { getCheapestProduct } from '../helpers/get-cheapest-product';
import { TSavedProjectsComplimentaryGroup } from '../types';

export type TState = {
  mainProduct: TProjectConfigurationGeneralItem | {};
  complementaryProducts: TProjectConfigurationGeneralItem[];
  complimentaryGroups: TSavedProjectsComplimentaryGroup[];
  checkedProducts: TCheckedType;
  services: TService[];
  isInit: boolean;
  addToCartIsSuccessful: boolean;
};

const defaultValues: TState = {
  mainProduct: {},
  complementaryProducts: [],
  complimentaryGroups: [],
  checkedProducts: [],
  services: [],
  isInit: false,
  addToCartIsSuccessful: false,
};

export const savedConfigurationCalculationState = atom({
  key: 'savedConfigurationCalculation',
  default: defaultValues,
});

export const useSavedProjectsCalculationState = () => {
  const { data } = useContext(DataContext);
  const [calculationFormState, setCalculationState] = useRecoilState(savedConfigurationCalculationState);

  useEffect(() => {
    if (!calculationFormState.isInit && data?.generalItems) {
      const complimentaryProductInfo = data.generalItems.filter(item => item.complementary) ?? [];
      const mainProduct = data.generalItems.find(item => !item.complementary);
      const services = data.services ?? [];
      const complimentaryGroups = data.complimentaryGroups ?? [];

      const groupsWithSelected = complimentaryGroups.map(group => {
        const cheapestProduct = getCheapestProduct(group.products);
        return {
          ...group,
          selectedProductId: cheapestProduct?.code ?? '',
          selectedVariantProductId: cheapestProduct?.code ?? null,
        };
      });

      const checkedComplimentaryProducts = complimentaryProductInfo.map(item => ({
        id: item.code,
        isChecked: true,
      }));

      const checkedComplimentaryGroupProducts = groupsWithSelected.flatMap(group =>
        group.products.map(product => ({
          id: product.code,
          isChecked: product.code === group.selectedProductId ? group.requiredToBuy ?? true : false,
        }))
      );

      const checkedProducts = [...checkedComplimentaryProducts, ...checkedComplimentaryGroupProducts];

      setCalculationState(prevState => ({
        ...prevState,
        mainProduct: mainProduct ?? {},
        complementaryProducts: complimentaryProductInfo,
        complimentaryGroups: groupsWithSelected,
        services,
        checkedProducts,
        isInit: true,
      }));
    }
  }, [calculationFormState.isInit, data, setCalculationState]);

  return {
    calculationFormState,
    setCalculationState,
  };
};
